import { ApplicationRef, DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { filter, first } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AppConfigStore } from '../app-config/app-config.store';
import { AppConfig } from '../config/app-config';
import { recoverStaleShell } from '../error/shell-recovery';
import { ROUTES } from '../routes';
import { isVersionBelow, isWellFormedVersion } from '../utils/version-utils';

/**
 * Shape of `ngsw-config.json`'s `appData` block, stamped at build time by
 * `scripts/inject-build-metadata.mjs`. `SwUpdate` types `appData` as a bare
 * `object` (it has no idea what shape a given app puts in there) — this is
 * this app's own contract with its build script, not an Angular API guarantee,
 * so every field is optional in case an older, pre-this-feature build is still
 * partially cached somewhere with no `appData` at all.
 */
export interface BuildAppData {
  version?: string;
  buildId?: string;
  buildTime?: string;
  changelog?: string;
}

/** Used when `environment.appUpdateFallback.checkIntervalMs` is missing or not a positive number. */
export const DEFAULT_UPDATE_CHECK_INTERVAL_MS = 6 * 60 * 60 * 1000;

/** Wait after checkForUpdate for VERSION_READY / download to settle. */
const UPDATE_RETRY_MS = 3000;

/** Survives reload: ignore re-offer of the build just activated. */
const APPLIED_UPDATE_KEY = 'jiopc.appUpdate.applied';

/** Survives navigation/reload: optional "Remind me later" snooze. */
const SNOOZE_KEY = 'jiopc.appUpdate.snooze';

interface AppliedUpdateMarker {
  version: string;
  hash?: string;
}

interface OptionalSnooze {
  version: string;
  hash?: string;
  untilMs: number;
}

/**
 * Detects when a newer build has been deployed (via `@angular/service-worker`)
 * and exposes a CMS version-gate prompt (`UpdatePromptComponent`).
 *
 * Flow:
 * - VERSION_READY → CMS refresh → force or optional dialog
 * - Remind Later → snooze until interval elapses or a newer version is ready
 * - Update → pull latest SW build, activate, reload
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly appRef = inject(ApplicationRef);
  private readonly appConfigStore = inject(AppConfigStore);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  /** Non-null once a new version has been downloaded and is ready to load. */
  readonly availableUpdate = signal<BuildAppData | null>(null);

  /** ngsw hash for {@link availableUpdate}. */
  private availableUpdateHash: string | null = null;

  /**
   * True while activate + reload is in flight. Blocks CTA morph; still accepts
   * a newer VERSION_READY so apply can land on the latest build.
   */
  private readonly applyInFlight = signal(false);

  /** Snapshot of force UI at apply start; kept stable until reload. */
  private forceUiLocked = false;

  /** True while the current URL is the dashboard. */
  private readonly onDashboard = signal(false);

  /** Profile "Check for updates" can surface the prompt off the dashboard. */
  private readonly revealedByManualCheck = signal(false);

  /**
   * True while CMS is being re-read for a gate decision. Keeps the dialog closed
   * so optional → forced does not remount mid-frame.
   */
  private readonly gateSettling = signal(false);

  /**
   * In-memory copy of optional snooze (also persisted). Primary source for
   * shouldShowPrompt so Remind Later works even if storage write fails, and
   * survives Dashboard navigation without relying on a storage re-read alone.
   */
  private readonly optionalSnooze = signal<OptionalSnooze | null>(null);

  /**
   * Running version is below CMS `minimumVersion`, and the prompt is allowed.
   * False when update is disabled, config never loaded, or the version is missing/malformed.
   */
  readonly versionGateOpen = computed(() => {
    if (!this.appConfigStore.hasConfigs()) return false;
    if (!this.appConfigStore.appUpdateEnabled()) return false;
    const minimumVersion = this.appConfigStore.minimumVersion();
    if (!isWellFormedVersion(minimumVersion)) return false;
    return isVersionBelow(AppConfig.VERSION_NAME, minimumVersion);
  });

  /**
   * A downloaded build whose stamped version is strictly newer than this one.
   */
  readonly hasInstallableUpdate = computed(() => {
    const next = this.availableUpdate()?.version;
    return isWellFormedVersion(next) && isVersionBelow(AppConfig.VERSION_NAME, next);
  });

  /**
   * Dialog when a newer build is downloaded.
   * Force when below floor + forceUpdate. Optional otherwise, unless snoozed
   * for this (or newer-than-snoozed) version.
   */
  readonly shouldShowPrompt = computed(() => {
    if (this.gateSettling()) return false;
    if (!this.appConfigStore.appUpdateEnabled() || !this.hasInstallableUpdate()) return false;
    if (!this.onDashboard() && !this.revealedByManualCheck()) return false;
    if (this.versionGateOpen() && this.appConfigStore.forceUpdate()) return true;
    if (this.applyInFlight()) return true;
    if (this.isOptionalSnoozed()) return false;
    return true;
  });

  /** Blocking prompt: no "Later", and leaving the dashboard is cancelled. */
  readonly isForceUpdate = computed(() => {
    if (this.applyInFlight()) return this.forceUiLocked;
    return this.shouldShowPrompt() && this.versionGateOpen() && this.appConfigStore.forceUpdate();
  });

  readonly updateTitle = computed(() => this.appConfigStore.updateTitle());
  readonly updateSubtitle = computed(() => this.appConfigStore.updateSubtitle());

  readonly isSupported = this.swUpdate.isEnabled;

  readonly isChecking = signal(false);

  private initialized = false;
  private restoringNavigation = false;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  /** Nested `withGateSettling` calls keep the dialog closed until the outermost finishes. */
  private gateSettlingDepth = 0;

  /** Call once, near app bootstrap (see `App.ngOnInit`). */
  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    clearLegacySessionKeys();
    this.optionalSnooze.set(readOptionalSnooze());

    this.watchDashboard();
    this.blockLeavingDashboardWhileForced();

    if (!this.swUpdate.isEnabled) return;

    document.addEventListener('visibilitychange', this.onVisibility);
    this.destroyRef.onDestroy(() => {
      document.removeEventListener('visibilitychange', this.onVisibility);
      if (this.pollTimer !== null) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    });

    this.swUpdate.versionUpdates.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      switch (event.type) {
        case 'VERSION_DETECTED':
          console.info('[AppUpdateService] version detected', {
            hash: event.version.hash,
            build: event.version.appData as BuildAppData | undefined,
          });
          break;
        case 'VERSION_READY':
          console.info('[AppUpdateService] version ready', {
            currentHash: event.currentVersion.hash,
            currentBuild: event.currentVersion.appData as BuildAppData | undefined,
            latestHash: event.latestVersion.hash,
            latestBuild: event.latestVersion.appData as BuildAppData | undefined,
          });
          void this.offerReadyUpdate(
            (event.latestVersion.appData as BuildAppData | undefined) ?? {},
            event.latestVersion.hash,
          );
          break;
        case 'VERSION_INSTALLATION_FAILED':
          console.error('[AppUpdateService] version installation failed', {
            hash: event.version.hash,
            build: event.version.appData as BuildAppData | undefined,
            error: event.error,
          });
          break;
        case 'NO_NEW_VERSION_DETECTED':
          console.debug('[AppUpdateService] no new version detected', {
            hash: event.version.hash,
            build: event.version.appData as BuildAppData | undefined,
          });
          break;
      }
    });

    this.swUpdate.unrecoverable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      console.error(`[AppUpdateService] unrecoverable state, clearing the stale shell: ${event.reason}`);
      void recoverStaleShell();
    });

    this.appRef.isStable
      .pipe(
        filter((stable) => stable),
        first(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.checkForUpdate();
        this.pollTimer = setInterval(() => {
          this.refreshSnoozeEvaluation();
          this.checkForUpdate();
        }, updateCheckIntervalMs());
      });
  }

  private readonly onVisibility = (): void => {
    if (document.visibilityState !== 'visible') return;
    this.refreshSnoozeEvaluation();
    this.checkForUpdate();
  };

  /**
   * A build is ready. Hold the dialog closed, re-read CMS, then publish so the
   * first paint is already optional or forced. During apply, still accept a
   * newer ready build so Update lands on latest.
   */
  private async offerReadyUpdate(build: BuildAppData, hash?: string): Promise<void> {
    if (shouldIgnoreReadyBuild(build, hash)) {
      console.info('[AppUpdateService] ignoring ready build already applied', { build, hash });
      return;
    }
    clearAppliedMarkerIfSupersededBy(build);

    if (this.applyInFlight()) {
      this.acceptNewerReadyBuild(build, hash);
      return;
    }

    await this.withGateSettling(async () => {
      if (this.applyInFlight()) {
        this.acceptNewerReadyBuild(build, hash);
        return;
      }
      if (shouldIgnoreReadyBuild(build, hash)) return;
      await this.appConfigStore.ensureLoaded(true);
      this.availableUpdate.set(build);
      this.availableUpdateHash = hash ?? null;
      this.clearSnoozeIfSupersededBy(build.version);
    });
  }

  /** During apply, replace availableUpdate only when the ready build is newer. */
  private acceptNewerReadyBuild(build: BuildAppData, hash?: string): void {
    const current = this.availableUpdate()?.version;
    if (
      isWellFormedVersion(build.version) &&
      (!isWellFormedVersion(current) || isVersionBelow(current, build.version))
    ) {
      this.availableUpdate.set(build);
      this.availableUpdateHash = hash ?? null;
    }
  }

  private async resyncGateForPendingBuild(): Promise<void> {
    await this.withGateSettling(() => this.appConfigStore.ensureLoaded(true));
  }

  private async withGateSettling(work: () => Promise<unknown>): Promise<void> {
    this.gateSettlingDepth += 1;
    this.gateSettling.set(true);
    try {
      await work();
    } catch (err) {
      console.warn('[AppUpdateService] CMS refresh for update gate failed', err);
    } finally {
      this.gateSettlingDepth = Math.max(0, this.gateSettlingDepth - 1);
      if (this.gateSettlingDepth === 0) {
        this.gateSettling.set(false);
      }
    }
  }

  private checkForUpdate(): void {
    if (!this.swUpdate.isEnabled) return;
    this.swUpdate.checkForUpdate().catch((err: unknown) => {
      console.warn('[AppUpdateService] checkForUpdate failed', err);
    });
  }

  /**
   * Profile "Check for updates" — clears optional snooze so the user can see
   * the offer again, then checks the service worker.
   */
  async checkForUpdateManually(): Promise<'unsupported' | 'up-to-date' | 'update-found' | 'error'> {
    if (!this.swUpdate.isEnabled) return 'unsupported';

    this.isChecking.set(true);
    try {
      this.clearOptionalSnooze();
      await this.appConfigStore.ensureLoaded(true);
      if (!this.hasInstallableUpdate()) {
        await this.swUpdate.checkForUpdate();
        await delay(UPDATE_RETRY_MS);
      }
      if (!this.appConfigStore.appUpdateEnabled() || !this.hasInstallableUpdate()) return 'up-to-date';
      this.revealedByManualCheck.set(true);
      return 'update-found';
    } catch (err) {
      console.warn('[AppUpdateService] manual checkForUpdate failed', err);
      return 'error';
    } finally {
      this.isChecking.set(false);
    }
  }

  /**
   * Optional "Remind me later" — snooze for {@link updateCheckIntervalMs}, not
   * cleared on Dashboard navigation. Force prompts ignore snooze.
   */
  dismissOptional(): void {
    if (this.isForceUpdate()) return;
    const version = this.availableUpdate()?.version;
    const hash = this.availableUpdateHash ?? undefined;
    // Prefer a well-formed ready version; fall back to hash-only snooze so a
    // missing appData.version cannot leave Remind Later as a no-op.
    if (!hash && (!version || !isWellFormedVersion(version))) {
      this.revealedByManualCheck.set(false);
      return;
    }
    const snooze: OptionalSnooze = {
      version: isWellFormedVersion(version) ? version! : AppConfig.VERSION_NAME,
      hash,
      untilMs: Date.now() + updateCheckIntervalMs(),
    };
    writeOptionalSnooze(snooze);
    this.optionalSnooze.set(snooze);
    this.revealedByManualCheck.set(false);
  }

  /**
   * User chose Update. Pull the latest SW build if a newer one is on the CDN,
   * activate it, then reload.
   */
  async applyUpdate(): Promise<'applied' | 'unavailable'> {
    if (!this.swUpdate.isEnabled) return 'unavailable';
    if (this.applyInFlight()) return 'applied';

    this.forceUiLocked = this.versionGateOpen() && this.appConfigStore.forceUpdate();
    this.applyInFlight.set(true);
    try {
      const ready = await this.ensureLatestReady();
      if (!ready) {
        this.applyInFlight.set(false);
        this.forceUiLocked = false;
        return 'unavailable';
      }
      const applied: AppliedUpdateMarker = {
        version: this.availableUpdate()?.version || AppConfig.VERSION_NAME,
        hash: this.availableUpdateHash ?? undefined,
      };
      await this.swUpdate.activateUpdate();
      writeAppliedUpdateMarker(applied);
      this.clearOptionalSnooze();
      this.availableUpdate.set(null);
      this.availableUpdateHash = null;
      // Keep applyInFlight / forceUiLocked until reload so the dialog does not
      // briefly remount as optional after a forced activate.
    } catch (err) {
      console.warn('[AppUpdateService] applyUpdate / activateUpdate failed, reloading anyway', err);
      window.location.reload();
      return 'applied';
    }
    window.location.reload();
    return 'applied';
  }

  /**
   * Ensure an installable build is ready, then check again so a newer CDN
   * deploy than the first waiting worker is preferred before activate.
   */
  private async ensureLatestReady(): Promise<boolean> {
    if (!this.hasInstallableUpdate()) {
      await this.safeCheck();
      await delay(UPDATE_RETRY_MS);
      if (!this.hasInstallableUpdate()) {
        await this.safeCheck();
        await delay(UPDATE_RETRY_MS);
      }
    }
    if (!this.hasInstallableUpdate()) return false;

    // Up to two passes: each checkForUpdate may surface a newer VERSION_READY.
    for (let pass = 0; pass < 2; pass++) {
      const before = this.availableUpdate()?.version;
      await this.safeCheck();
      await delay(UPDATE_RETRY_MS);
      const after = this.availableUpdate()?.version;
      if (
        !isWellFormedVersion(after) ||
        !isWellFormedVersion(before) ||
        !isVersionBelow(before, after)
      ) {
        break;
      }
    }
    return this.hasInstallableUpdate();
  }

  private async safeCheck(): Promise<boolean> {
    try {
      return await this.swUpdate.checkForUpdate();
    } catch (err) {
      console.warn('[AppUpdateService] checkForUpdate failed', err);
      return false;
    }
  }

  private watchDashboard(): void {
    this.noteDashboardUrl(this.router.url);
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (!(event instanceof NavigationEnd)) return;
      this.noteDashboardUrl(event.urlAfterRedirects);
    });
  }

  private noteDashboardUrl(url: string): void {
    const onDashboard = isDashboardUrl(url);
    this.onDashboard.set(onDashboard);
    if (!onDashboard) return;
    // Do not clear optional snooze on Dashboard return — only re-evaluate expiry.
    this.refreshSnoozeEvaluation();
    this.revealedByManualCheck.set(false);
    if (this.hasInstallableUpdate()) {
      void this.resyncGateForPendingBuild();
    } else {
      this.checkForUpdate();
    }
  }

  private blockLeavingDashboardWhileForced(): void {
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (!(event instanceof NavigationStart) || this.restoringNavigation) return;
      if (!this.isForceUpdate() || !this.onDashboard()) return;
      if (isDashboardUrl(event.url)) return;
      const stayOn = this.router.url;
      this.restoringNavigation = true;
      void this.router.navigateByUrl(stayOn, { replaceUrl: true }).finally(() => {
        this.restoringNavigation = false;
      });
    });
  }

  /**
   * Optional snooze active unless expired or a strictly newer build is ready.
   * Same hash or same/older version stays snoozed. If comparison is inconclusive,
   * honor the snooze so Dashboard return cannot re-prompt.
   */
  private isOptionalSnoozed(): boolean {
    const snooze = this.optionalSnooze() ?? readOptionalSnooze();
    if (!snooze) return false;
    if (Date.now() >= snooze.untilMs) return false;

    const readyHash = this.availableUpdateHash;
    if (snooze.hash && readyHash && snooze.hash === readyHash) return true;

    const readyVersion = this.availableUpdate()?.version;
    if (isWellFormedVersion(snooze.version) && isWellFormedVersion(readyVersion)) {
      // Snoozed while ready is not strictly newer than what the user dismissed.
      return !isVersionBelow(snooze.version, readyVersion);
    }

    // Have an unexpired snooze but cannot compare — keep quiet (UX over re-prompt).
    return true;
  }

  /** Drop expired snooze; keep an active one across Dashboard visits. */
  private refreshSnoozeEvaluation(): void {
    const snooze = this.optionalSnooze() ?? readOptionalSnooze();
    if (!snooze) {
      this.optionalSnooze.set(null);
      return;
    }
    if (Date.now() >= snooze.untilMs) {
      this.clearOptionalSnooze();
      return;
    }
    // Re-hydrate memory from storage if needed (e.g. another tab updated it).
    if (!this.optionalSnooze()) {
      this.optionalSnooze.set(snooze);
    }
  }

  private clearOptionalSnooze(): void {
    clearOptionalSnoozeStorage();
    this.optionalSnooze.set(null);
  }

  /** If a newer build than the snoozed version is ready, drop the snooze. */
  private clearSnoozeIfSupersededBy(readyVersion: string | undefined): void {
    const snooze = this.optionalSnooze() ?? readOptionalSnooze();
    if (
      snooze &&
      isWellFormedVersion(snooze.version) &&
      isWellFormedVersion(readyVersion) &&
      isVersionBelow(snooze.version, readyVersion)
    ) {
      this.clearOptionalSnooze();
    }
  }
}

function updateCheckIntervalMs(): number {
  const configured = environment.appUpdateFallback?.checkIntervalMs;
  return typeof configured === 'number' && Number.isFinite(configured) && configured > 0
    ? configured
    : DEFAULT_UPDATE_CHECK_INTERVAL_MS;
}

function isDashboardUrl(url: string): boolean {
  const path = url.split(/[?#]/)[0];
  const dashboard = `/${ROUTES.DASHBOARD}`;
  return path === dashboard || path.startsWith(`${dashboard}/`);
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function readAppliedUpdateMarker(): AppliedUpdateMarker | null {
  try {
    const raw = localStorage.getItem(APPLIED_UPDATE_KEY) ?? sessionStorage.getItem(APPLIED_UPDATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AppliedUpdateMarker;
    if (!parsed || typeof parsed.version !== 'string') return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeAppliedUpdateMarker(marker: AppliedUpdateMarker): void {
  try {
    localStorage.setItem(APPLIED_UPDATE_KEY, JSON.stringify(marker));
    sessionStorage.removeItem(APPLIED_UPDATE_KEY);
  } catch {
    try {
      sessionStorage.setItem(APPLIED_UPDATE_KEY, JSON.stringify(marker));
    } catch {
      // best-effort
    }
  }
}

function clearAppliedUpdateMarker(): void {
  try {
    localStorage.removeItem(APPLIED_UPDATE_KEY);
    sessionStorage.removeItem(APPLIED_UPDATE_KEY);
  } catch {
    // ignore
  }
}

function clearAppliedMarkerIfSupersededBy(build: BuildAppData): void {
  const marker = readAppliedUpdateMarker();
  if (
    marker &&
    isWellFormedVersion(marker.version) &&
    isWellFormedVersion(build.version) &&
    isVersionBelow(marker.version, build.version)
  ) {
    clearAppliedUpdateMarker();
  }
}

function shouldIgnoreReadyBuild(build: BuildAppData, hash?: string): boolean {
  const marker = readAppliedUpdateMarker();
  if (!marker) return false;

  if (hash && marker.hash && hash === marker.hash) return true;

  if (
    isWellFormedVersion(marker.version) &&
    isWellFormedVersion(build.version) &&
    !isVersionBelow(marker.version, build.version) &&
    !isVersionBelow(AppConfig.VERSION_NAME, marker.version)
  ) {
    return true;
  }

  return false;
}

function readOptionalSnooze(): OptionalSnooze | null {
  try {
    const raw = localStorage.getItem(SNOOZE_KEY) ?? sessionStorage.getItem(SNOOZE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as OptionalSnooze;
    if (!parsed || typeof parsed.version !== 'string' || typeof parsed.untilMs !== 'number') {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeOptionalSnooze(snooze: OptionalSnooze): void {
  const raw = JSON.stringify(snooze);
  try {
    localStorage.setItem(SNOOZE_KEY, raw);
  } catch {
    // fall through to sessionStorage
  }
  try {
    sessionStorage.setItem(SNOOZE_KEY, raw);
  } catch {
    // in-memory signal still holds the snooze for this tab
  }
}

function clearOptionalSnoozeStorage(): void {
  try {
    localStorage.removeItem(SNOOZE_KEY);
  } catch {
    // ignore
  }
  try {
    sessionStorage.removeItem(SNOOZE_KEY);
  } catch {
    // ignore
  }
}

function clearLegacySessionKeys(): void {
  try {
    sessionStorage.removeItem('jiopc.appUpdate.suppressOptional');
    // Migrate old session applied marker once.
    const legacy = sessionStorage.getItem('jiopc.appUpdate.applied');
    if (legacy && !localStorage.getItem(APPLIED_UPDATE_KEY)) {
      localStorage.setItem(APPLIED_UPDATE_KEY, legacy);
    }
    sessionStorage.removeItem('jiopc.appUpdate.applied');
  } catch {
    // ignore
  }
}
